import type { ContentSection } from '../../../../amplienceTypes/schemas/exported/section-schema';
import type { ReactElement } from 'react';

import dynamic from 'next/dynamic';

import { useStaticContext } from '../../../../utilities/context/static/StaticContext';
import { removeLegacyModifierClassNames } from '../../styles/utils';
import type { ClassName } from '~/amplienceTypes/partials/exported/element-attributes-schema';
import { CollectionSectionTemplate } from '~/campaign/collections/templates/CollectionSectionTemplate';
import { hasClassName } from '~/utilities/amplience/hasClassName';

const Categories = dynamic(async () => (await import('./Categories')).Categories);
const Cards = dynamic(async () => (await import('./Cards')).Cards);
const Flowbox = dynamic(async () => (await import('./Flowbox')).Flowbox);
const ProfileToggleSection = dynamic(
  async () => (await import('./ProfileToggleSection')).ProfileToggleSection
);
const SectionTemplate = dynamic(async () => (await import('./SectionTemplate')).SectionTemplate);

export const Section = (props: ContentSection): ReactElement => {
  const { layout, attributes } = props;
  const classNames = attributes?.classNames as ClassName;
  const { accessibilityLabels } = useStaticContext();

  switch (true) {
    case layout?.startsWith('Collection'):
      return <CollectionSectionTemplate {...props} />;
    case layout === 'HP Categories':
      return (
        <Categories
          {...props}
          ariaRole={accessibilityLabels?.hpCategories?.role}
          ariaLabel={accessibilityLabels?.hpCategories?.label}
        />
      );
    case layout === 'HP Card Overview':
      return (
        <Cards
          {...props}
          ariaRole={accessibilityLabels?.hpCardOverview?.role}
          ariaLabel={accessibilityLabels?.hpCardOverview?.label}
        />
      );
    case hasClassName(classNames, 'row--flowbox-homepage'):
      return (
        <Flowbox
          {...props}
          ariaRole={accessibilityLabels?.hpSection?.role}
          ariaLabel={accessibilityLabels?.hpSection?.label}
        />
      );
    case hasClassName(classNames, 'is-profile-toggle'):
      return <ProfileToggleSection {...props} />;
    default:
      return (
        <SectionTemplate
          {...removeLegacyModifierClassNames(props)}
          ariaRole={accessibilityLabels?.hpSection?.role}
          ariaLabel={accessibilityLabels?.hpSection?.label}
        />
      );
  }
};
